import Analytics from '@/utils/analytics';
import AsyncStorage from '@react-native-async-storage/async-storage';
import moment from 'moment-timezone';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  AuthStatus,
  ChatStatus,
  ListDisplayMode,
  PaymentsStatus,
  PermissionAction,
  PermissionSubject,
  SalesRepOverrideMode,
  SmartOrderRole,
  ViewableBy,
  ViewableByWithNone,
} from '@/enums';
import { businessEmployeeAccessControlFactory } from '@/utils/access-control';
import { captureException } from '@/utils/sentry';
import {
  CommissionSettings,
  ConsumptionTax,
  DynamicConfig,
} from './dynamic-config';
import { EmployeeConfig } from '@domain/configuration/types/employee-config';
import {
  EmployeeRole,
  UserContext_EmployeeQuery,
  UserContext_EmployeeQueryVariables,
  UserContextSupplierEmployeeFragment,
  UserContextSupplierFragment,
  UserContextUserContactInfoFragment,
  UserContextUserFragment,
  UserContextUserSupplierEmployeeOverridesFragment,
  VariantPackCostType,
} from '@/types';
import { Fennel } from '@/analytics/fennel/fennel';
import { getValueFromConfigs } from '@/contexts/dynamic-config-utils';
import {
  GUEST_USER_SETTINGS_KEY,
  USER_SETTINGS_KEY,
  UserSettingsAdapter,
} from '@/adapters/user-settings';
import { isOfflineModeAllowed } from '@/utils/offline-mode';
import { ItemSortOrder } from './item-context-types';
import { normalizeQuotes } from '@/utils/string';
import { partnerSupplier } from '@/utils/environment';
import {
  performanceTracingRuntimeConfig,
  resolvePerformanceTracingRuntimeConfig,
} from '@/analytics/fennel/performance/runtime-config';
import { Platform } from 'react-native';
import {
  resolveSentryTracingRuntimeConfig,
  sentryTracingRuntimeConfig,
} from '@/utils/sentry/runtime-config';
import { ToastOptions } from '@/components/toast';
import { useAuthContext } from './auth-context';
import { useAuthQueries } from '@/hooks/use-auth-queries';
import { useAuthStackContext } from './auth-stack-context';
import { useAutoLoginParams } from '@/hooks/use-auto-login-params';
import { useOfflineModeActiveWithoutDelay } from '@/hooks/use-offline-mode-active';
import { useOfflineUserContext } from '@/hooks/use-offline-data';
import { USER_QUERY } from './user-context-queries';
import { useSentrySessionTracking } from '@/hooks/use-sentry-session-tracking';
import { useSyncUserData } from '@/hooks/use-sync-user-data';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export interface SmartOrderSettings {
  assign_to_others?: boolean;
  notify?: boolean;
  order_agent_only?: boolean;
  smart_order_role?: SmartOrderRole;
  training_mode_enabled?: boolean;
  unknown_contact_triage?: boolean;
}

export interface CreditRequestSettings {
  request_ineligible_items_enabled: boolean;
}

export interface EmployeePermissions {
  allow_commission?: boolean;
  credit_request_settings?: CreditRequestSettings;
  smart_order_settings?: SmartOrderSettings;
  srt_price_editing?: PermissionAction;
}

export interface ChatRestaurantMap {
  [chatUUID: string]: string;
}

interface SupplierDynamicConfigMap {
  [supplierUUID: string]: DynamicConfig;
}

export interface EmployeeRestaurant {
  accountID: string | null;
  addressString: string;
  appPaymentsEnabled: boolean;
  carouselHoldout: boolean;
  categoryImagesEnabled: boolean;
  chatDynamicConfig: DynamicConfig;
  chatStatus: ChatStatus;
  chatUUIDs: string[];
  commissionSettings: CommissionSettings | null;
  consumptionTax: ConsumptionTax[] | null;
  countryCode: string | null;
  customerCostType: VariantPackCostType | null;
  displayItemPrices: boolean;
  employeeRole: EmployeeRole | null;
  hidePlaceholderImages: boolean;
  largeScreenModalHoldout: boolean;
  name: string;
  onDemandPaymentStatementEnabled: boolean;
  onHold: boolean;
  orderReviewRequired: boolean;
  paymentsOnlyEnabled: boolean;
  payUponOrdering: boolean;
  requirePaymentMethodOnOrderPlacement: boolean;
  salesRepGrossProfitEnabled: boolean;
  salesRepIgnoreNegativeInGrossProfitTotal: boolean;
  salesRepModeEnabled: boolean;
  salesRepPeakPriceProtection: boolean;
  salesRepPriceFloorMode: VariantPackCostType | null;
  salesRepPricingEnabled: boolean;
  showChatbot: boolean;
  showCreditRequest: boolean;
  showCrmActivities: boolean;
  showMenuCosting: boolean;
  showMenuOrderGuideGeneration: boolean;
  showOfflineMode: boolean;
  showPaymentLinks: boolean;
  showPaymentsTab: boolean;
  showProspectFinder: boolean;
  showTotalSalesDashboard: boolean;
  smartOrderEnabled: boolean;
  supplierName: string;
  supplierPaymentsStatus: PaymentsStatus;
  supplierUUID: string;
  tagPrintingEnabled: boolean;
  timezone: string;
  transactionFeeName: string | null;
  uuid: string;
}

export type SortOrders = ItemSortOrder | SearchSortOrder;

export enum SearchSortOrder {
  Relevance = 'RELEVANCE',
  // eslint-disable-next-line typescript-sort-keys/string-enum
  Alphabetical = 'ALPHABETICAL',
  AlphabeticalReverse = 'ALPHABETICAL_REVERSE',
  ItemCode = 'ITEM_CODE',
  ItemCodeReverse = 'ITEM_CODE_REVERSE',
  PurchasedDate = 'PURCHASED_DATE',
}

export const BASE_SORT_ORDERS = Object.values(ItemSortOrder).filter(
  (order) =>
    order !== ItemSortOrder.Custom && order !== ItemSortOrder.PurchasedDate,
);
export const GUEST_MODE_SORT_ORDERS = [
  ItemSortOrder.Alphabetical,
  ItemSortOrder.AlphabeticalReverse,
];
export const ALL_ITEM_SORT_ORDERS = Object.values(ItemSortOrder);
export const SEARCH_SORT_ORDERS = Object.values(SearchSortOrder);

export enum AlertKeys {
  OrderGuideButton = 'ORDER_GUIDE_BUTTON',
  SubscriptionButton = 'SUBSCRIPTION_BUTTON',
}

type AlertsSeen = {
  [name in AlertKeys]: boolean;
};

export interface UserSettings {
  catalogSortOrder: ItemSortOrder;
  inInboundTrainingMode: boolean;
  isDescriptionVisible: boolean;
  largeItemListDisplayMode: ListDisplayMode;
  orderGuideSortOrder: ItemSortOrder;
  searchSortOrder: SearchSortOrder;
  selectedRestaurantUUID: string | null;
  selectedSupplierUUID: string | null;
  shouldFlashlightActivate: boolean;
  showSalesRepUI: boolean;
}

const INITIAL_USER_SETTINGS: UserSettings = {
  catalogSortOrder: ItemSortOrder.Alphabetical,
  inInboundTrainingMode: false,
  isDescriptionVisible: true,
  largeItemListDisplayMode: ListDisplayMode.List,
  orderGuideSortOrder: ItemSortOrder.Alphabetical,
  searchSortOrder: SearchSortOrder.Relevance,
  selectedRestaurantUUID: null,
  selectedSupplierUUID: null,
  shouldFlashlightActivate: true,
  // Defaults to true but remains gated by the sales_rep_*_enabled flags
  showSalesRepUI: true,
};

export const INITIAL_ALERTS_SEEN: AlertsSeen = {
  [AlertKeys.OrderGuideButton]: false,
  [AlertKeys.SubscriptionButton]: false,
};

export const ALERTS_SEEN_KEY = 'tooltipsSeen';

interface CheckAuthStatusOptions {
  errorToastOptions?: ToastOptions;
  reset?: boolean;
}

export interface LocalUserUpdates {
  contactEmail?: string;
  contactPhoneNumber?: string;
  locale?: string;
  loginEmail?: string;
  name?: string;
}

export function getLocalUserUpdatesFromEmployee(
  employee: { contact_info: UserContextUserContactInfoFragment; name: string },
  override?: UserContextUserSupplierEmployeeOverridesFragment | null,
): LocalUserUpdates {
  return {
    contactEmail:
      override?.contact_emails[0] ?? employee.contact_info.email ?? undefined,
    contactPhoneNumber:
      override?.contact_phone_numbers[0] ??
      employee.contact_info.phone_number ??
      undefined,
    loginEmail: employee.contact_info.login_email ?? undefined,
    name: override?.name ?? employee.name,
  };
}

interface BaseUserContextType {
  authStatus: AuthStatus;
  checkAuthStatus: (options?: CheckAuthStatusOptions) => Promise<void>;
  isGuestMode: boolean;
  previousAuthStatus: AuthStatus | null;
  refetch: () => void;
}

interface NotAuthenticatedContextType extends BaseUserContextType {
  authStatus: AuthStatus.NotAuthenticated;
}

interface NotAuthorizedUserContextType extends BaseUserContextType {
  authStatus: AuthStatus.NotAuthorized;
  employeeCan: (
    action: PermissionAction,
    subject: PermissionSubject,
  ) => boolean;
}

export interface AuthenticatedUserContextType extends BaseUserContextType {
  authStatus: AuthStatus.Authenticated;
  canCreateAccount: boolean;
  // Used to map chatUUIDs to restaurantUUIDs for white label notifications
  // TODO - at some point merge this data with ChatsContext
  chatRestaurantMap: ChatRestaurantMap;
  commissionSettings: CommissionSettings | null;
  creditRequestSettings: CreditRequestSettings | null;
  employeeCan: (
    action: PermissionAction,
    subject: PermissionSubject,
  ) => boolean;
  overrideChatStatus: (restaurantUUID: string) => void;
  restaurantRelationships: EmployeeRestaurant[];
  restaurantUUIDToRelationshipMap: Record<string, EmployeeRestaurant>;
  selectedRestaurantRelationship: EmployeeRestaurant;
  selectedRestaurantUUID: string;
  selectRestaurant: (restaurantUUID: string) => void;
  smartOrderSettings: SmartOrderSettings | null;
  supplierDynamicConfigMap: SupplierDynamicConfigMap;
  supplierEmployeeMap: Record<string, UserContextSupplierEmployeeFragment>;
  supplierEmployeeRole: EmployeeRole | null;
  supplierMap: Record<string, UserContextSupplierFragment>;
  translationsEnabled: boolean;
  updateUserLocally: (updates: LocalUserUpdates) => void;
  user: UserContextUserFragment;
}

export interface SmartOrderAuthenticatedUserContextType
  extends AuthenticatedUserContextType {
  orderUUID?: string | null;
}

export interface OfflineUserContextType extends BaseUserContextType {
  authStatus: AuthStatus.Offline;
  selectedRestaurantRelationship: EmployeeRestaurant;
  supplierEmployeeRole: EmployeeRole | null;
}

export interface GuestUserContextType extends BaseUserContextType {
  authStatus: AuthStatus.NotAuthenticated;
  chatRestaurantMap: ChatRestaurantMap;
  commissionSettings: CommissionSettings | null;
  creditRequestSettings: CreditRequestSettings | null;
  employeeCan: (
    action: PermissionAction,
    subject: PermissionSubject,
  ) => boolean;
  restaurantRelationships: EmployeeRestaurant[];
  restaurantUUIDToRelationshipMap: Record<string, EmployeeRestaurant>;
  selectedRestaurantRelationship: EmployeeRestaurant;
  selectedRestaurantUUID: string;
  smartOrderSettings: SmartOrderSettings | null;
  supplierDynamicConfigMap: SupplierDynamicConfigMap;
  translationsEnabled: boolean;
}

export type UserContextType =
  | NotAuthenticatedContextType
  | NotAuthorizedUserContextType
  | AuthenticatedUserContextType
  | OfflineUserContextType
  | GuestUserContextType;

/**
 * Type guard to check if user context is fully authenticated (NOT offline, NOT guest)
 * This narrows to AuthenticatedUserContextType which has full user data and methods
 */
export function isAuthenticatedUserContext(
  context: UserContextType | null,
): context is AuthenticatedUserContextType {
  return (
    context !== null &&
    !context.isGuestMode &&
    context.authStatus === AuthStatus.Authenticated
  );
}

export const UserContext = createContext<UserContextType | null>(null);
export interface UserSettingsContextType {
  alertsSeen: { [name in AlertKeys]: boolean };
  catalogSortOrder: ItemSortOrder;
  inInboundTrainingMode: boolean;
  isDescriptionVisible: boolean;
  largeItemListDisplayMode: ListDisplayMode;
  searchSortOrder: SearchSortOrder;
  setAlertSeen: (name: AlertKeys) => void;
  setCatalogSortOrder: (sortOrder: ItemSortOrder) => void;
  setInInboundTrainingMode: (nextValue: boolean) => void;
  setIsDescriptionVisible: (isDescriptionVisible: boolean) => void;
  setLargeItemListDisplayMode: (displayMode: ListDisplayMode) => void;
  setSearchSortOrder: (sortOrder: SearchSortOrder) => void;
  setShouldFlashlightActivate: (shouldFlashlightActivate: boolean) => void;
  setShowSalesRepUI: (setShowSalesRepUI: boolean) => void;
  shouldFlashlightActivate: boolean;
  showSalesRepUI: boolean;
}

export const UserSettingsContext =
  createContext<UserSettingsContextType | null>(null);

export const getUserContextVariables = (
  userUUID: string,
  filterExternalSessionsEnabled = false,
) => {
  const queryVariables: UserContext_EmployeeQueryVariables = {
    businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
    employeeChatsFilter: {
      business_organization_uuid: {
        _eq: partnerSupplier.businessOrganizationUUID,
      },
    },
    userUUID,
  };

  if (filterExternalSessionsEnabled) {
    queryVariables.employeeChatsFilter.external_sessions_enabled = {
      _eq: true,
    };
  }

  return queryVariables;
};

const determineCreditRequestVisibility = (
  isSupplierEmployee: boolean,
  viewableBy: ViewableBy,
) => {
  switch (viewableBy) {
    case ViewableBy.ALL:
      return true;
    case ViewableBy.RESTAURANT:
      return !isSupplierEmployee;
    case ViewableBy.SUPPLIER:
      return isSupplierEmployee;
    default:
      return false;
  }
};

export const getSavedUserSettings = async (): Promise<UserSettings> => {
  const adapter = new UserSettingsAdapter<UserSettings>(USER_SETTINGS_KEY);
  const savedUserSettings = await adapter.fetch();
  return savedUserSettings
    ? { ...INITIAL_USER_SETTINGS, ...savedUserSettings }
    : INITIAL_USER_SETTINGS;
};

/**
 * Determine whether or not the current user can view prices based on their relationship to the restaurant and supplier
 * @param {Partial<Chats>} chat - Relationship to restaurant
 * @param {Partial<Businesses>} supplier - Relationship to supplier
 * @returns {boolean} true if the user should be able to see prices, false otherwise
 *
 * We maintain the following price visibility hierarchy:
 *   1. Chat Prospect level setting for prospective users
 *   2. Supplier Prospect level setting for prospective users
 *   3. Chat level setting
 *   4. Supplier level setting
 *   5. false (default)
 */
export function canDisplayItemPrices(
  displayItemPrices: boolean,
  chatStatus: ChatStatus,
  chatDynamicConfig: DynamicConfig,
  supplierDynamicConfig: DynamicConfig,
  isSupplierEmployee: boolean,
) {
  if (chatStatus === ChatStatus.PROSPECT) {
    // TODO: https://usepepper.atlassian.net/browse/DSR-2814
    const viewableBy =
      chatDynamicConfig.prospect_item_prices_viewable_by ??
      supplierDynamicConfig.prospect_item_prices_viewable_by;

    switch (viewableBy) {
      case ViewableByWithNone.NONE:
        return false;
      case ViewableByWithNone.ALL:
        return true;
      case ViewableByWithNone.SUPPLIER:
        return isSupplierEmployee;
    }

    return false;
  }

  return displayItemPrices;
}

export interface SupplierScopedUserFields {
  canCreateAccount: boolean;
  commissionSettings: CommissionSettings | null;
  creditRequestSettings: CreditRequestSettings | null;
  employeeCan: (
    action: PermissionAction,
    subject: PermissionSubject,
  ) => boolean;
  smartOrderSettings: SmartOrderSettings | null;
  supplierEmployeeRole: EmployeeRole | null;
}

interface DeriveSupplierScopedUserFieldsParams {
  selectedRestaurantRelationship: EmployeeRestaurant;
  supplierEmployeeMap: Record<string, UserContextSupplierEmployeeFragment>;
  supplierMap: Record<string, UserContextSupplierFragment>;
  supplierUUID: string;
}

/*
 * Derives every user-context field scoped to a single supplier from that
 * supplier's `businesses_employees` record.
 *
 * Used to help set these values on user contexts to ensure they update if the
 * selected supplier changes in the case of multi-supplier apps.
 */
export const deriveSupplierScopedUserFields = ({
  selectedRestaurantRelationship,
  supplierEmployeeMap,
  supplierMap,
  supplierUUID,
}: DeriveSupplierScopedUserFieldsParams): SupplierScopedUserFields => {
  const supplierBusinessEmployee = supplierEmployeeMap[supplierUUID];
  const employeePermissions: EmployeePermissions =
    supplierBusinessEmployee?.employee_permissions
      ? supplierBusinessEmployee.employee_permissions
      : {};

  return {
    canCreateAccount: Boolean(
      supplierMap[supplierUUID]?.supplier_metadata
        ?.app_account_creation_enabled,
    ),
    // If employee is allowed, then commission settings are available
    commissionSettings: employeePermissions.allow_commission
      ? selectedRestaurantRelationship.commissionSettings
      : null,
    creditRequestSettings: employeePermissions.credit_request_settings || null,
    employeeCan: businessEmployeeAccessControlFactory(employeePermissions),
    smartOrderSettings: employeePermissions.smart_order_settings || null,
    supplierEmployeeRole: supplierBusinessEmployee
      ? (supplierBusinessEmployee.employee_role as EmployeeRole)
      : null,
  };
};

interface Props {
  children?: React.ReactNode;
}

export const UserContextProvider: React.FC<Props> = ({ children }) => {
  const autoLoginParams = useAutoLoginParams();
  const {
    authenticatedUserUUID,
    authStatus,
    checkAuthStatus,
    previousAuthStatus,
  } = useAuthContext();

  const [userSettings, setUserSettings] = useState<UserSettings>(
    INITIAL_USER_SETTINGS,
  );
  const [alertsSeen, setAlertsSeen] = useState<AlertsSeen>(INITIAL_ALERTS_SEEN);

  const queryVariables = useMemo<UserContext_EmployeeQueryVariables>(() => {
    return getUserContextVariables(
      authenticatedUserUUID!,
      autoLoginParams?.type === 'PUNCHOUT',
    );
  }, [authenticatedUserUUID, autoLoginParams]);

  const isOfflineModeActive = useOfflineModeActiveWithoutDelay();
  const [hasFetchedUserContext, setHasFetchedUserContext] = useState(false);

  /*
   * Avoid caching the user context query to improve loading times for
   * employees connected to many chats.
   * Manually manage changes that would otherwise update cache using local
   * state.
   */
  const { data, loading, refetch, hasResponse } = useWrappedQuery<
    UserContext_EmployeeQuery,
    UserContext_EmployeeQueryVariables
  >(USER_QUERY, {
    fetchPolicy: 'no-cache',
    skip:
      !queryVariables.userUUID ||
      (isOfflineModeActive && !hasFetchedUserContext),
    variables: queryVariables,
  });

  // Problem: we don't want to fetch the user context when the device is offline,
  // and we do want to fetch it once they're back online, but we do not want to
  // fetch it every time they go between the two. We only want to fetch it once.
  // Solution: Keep track of whether we've fetched the user context at least once,
  // and include that in the skip variable.
  useEffect(() => {
    setHasFetchedUserContext(hasResponse);
  }, [hasResponse]);

  const { data: offlineContextData, loading: loadingOfflineContext } =
    useOfflineUserContext(authenticatedUserUUID, data);

  const userContextData = useMemo(() => {
    if (isOfflineModeActive && offlineContextData) {
      return offlineContextData;
    }

    return data;
  }, [data, isOfflineModeActive, offlineContextData]);

  // Track restaurants promoted to ACTIVE during the current auth session
  // instead of relying on direct cache writes for the user query.
  const [promotedRestaurantUUIDs, setPromotedRestaurantUUIDs] = useState<
    Set<string>
  >(new Set());
  const overrideChatStatus = useCallback((restaurantUUID: string) => {
    setPromotedRestaurantUUIDs((prev) => {
      if (prev.has(restaurantUUID)) {
        return prev;
      }

      const next = new Set(prev);
      next.add(restaurantUUID);
      return next;
    });
  }, []);
  const [localUserUpdates, setLocalUserUpdates] =
    useState<LocalUserUpdates | null>(null);
  const updateUserLocally = useCallback((updates: LocalUserUpdates) => {
    setLocalUserUpdates((prev) => ({ ...prev, ...updates }));
  }, []);

  // Always use latest user info data from the server
  useEffect(() => {
    setLocalUserUpdates(null);
  }, [data]);

  // only reset chat overrides when the user UUID changes as statuses are
  // updated async after orders are placed
  useEffect(() => {
    setPromotedRestaurantUUIDs(new Set());
  }, [authenticatedUserUUID]);

  useAuthQueries(
    authStatus,
    previousAuthStatus,
    userSettings.selectedSupplierUUID,
  );
  useSyncUserData(
    userContextData?.employees_by_pk || null,
    userSettings.selectedSupplierUUID,
    updateUserLocally,
  );

  useEffect(() => {
    // Hydrate user settings from AsyncStorage
    (async () => {
      try {
        setUserSettings(await getSavedUserSettings());

        const savedAlertsSeen = await AsyncStorage.getItem(ALERTS_SEEN_KEY);
        const parsedSavedValues = savedAlertsSeen
          ? JSON.parse(savedAlertsSeen)
          : {};

        setAlertsSeen({ ...INITIAL_ALERTS_SEEN, ...parsedSavedValues });
      } catch (e) {
        captureException(e);
      }
    })();
  }, []);

  const setUserSettingsCallback = useCallback(
    async (settings: Partial<UserSettings>) => {
      try {
        const adapter = new UserSettingsAdapter<UserSettings>(
          USER_SETTINGS_KEY,
        );
        const savedUserSettings = await adapter.fetch();
        const nextUserSettings = {
          ...(savedUserSettings || INITIAL_USER_SETTINGS),
          ...settings,
        };

        await adapter.save(nextUserSettings);

        setUserSettings(nextUserSettings);
      } catch (e) {
        captureException(e);
      }
    },
    [],
  );

  const setAlertsSeenCallback = useCallback(async (name: AlertKeys) => {
    try {
      const savedAlertsSeen = await AsyncStorage.getItem(ALERTS_SEEN_KEY);
      const parsedSavedValues = savedAlertsSeen
        ? JSON.parse(savedAlertsSeen)
        : {};

      const nextAlerts = {
        ...INITIAL_ALERTS_SEEN,
        ...parsedSavedValues,
        [name]: true,
      };

      await AsyncStorage.setItem(ALERTS_SEEN_KEY, JSON.stringify(nextAlerts));

      setAlertsSeen(nextAlerts);
    } catch (e) {
      captureException(e);
    }
  }, []);

  const settingsValue = useMemo<UserSettingsContextType>(() => {
    return {
      alertsSeen,
      catalogSortOrder:
        userSettings.catalogSortOrder || ItemSortOrder.Alphabetical,
      inInboundTrainingMode: userSettings.inInboundTrainingMode,
      isDescriptionVisible: userSettings.isDescriptionVisible ?? true,
      largeItemListDisplayMode:
        userSettings.largeItemListDisplayMode || ListDisplayMode.List,
      searchSortOrder:
        userSettings.searchSortOrder || SearchSortOrder.Relevance,
      setAlertSeen: setAlertsSeenCallback,
      setCatalogSortOrder: (sortOrder: ItemSortOrder) => {
        setUserSettingsCallback({ catalogSortOrder: sortOrder });
      },
      setInInboundTrainingMode: (nextValue: boolean) => {
        setUserSettingsCallback({ inInboundTrainingMode: nextValue });
      },
      setIsDescriptionVisible: (isDescriptionVisible: boolean) => {
        setUserSettingsCallback({ isDescriptionVisible });
      },
      setLargeItemListDisplayMode: (displayMode: ListDisplayMode) => {
        setUserSettingsCallback({ largeItemListDisplayMode: displayMode });
      },
      setSearchSortOrder: (sortOrder: SearchSortOrder) => {
        setUserSettingsCallback({ searchSortOrder: sortOrder });
      },
      setShouldFlashlightActivate: (shouldFlashlightActivate: boolean) => {
        setUserSettingsCallback({ shouldFlashlightActivate });
      },
      setShowSalesRepUI: (showSalesRepUI: boolean) => {
        setUserSettingsCallback(
          showSalesRepUI
            ? {
                largeItemListDisplayMode: ListDisplayMode.List,
                showSalesRepUI,
              }
            : { showSalesRepUI },
        );
      },
      shouldFlashlightActivate: userSettings.shouldFlashlightActivate,
      showSalesRepUI: userSettings.showSalesRepUI,
    };
  }, [
    setAlertsSeenCallback,
    setUserSettingsCallback,
    alertsSeen,
    userSettings,
  ]);

  const value = useMemo<UserContextType | null>(() => {
    // Show loading state when auth status is unknown (initial load or reconnecting)
    // or when user query is loading (but not when we know user is NotAuthenticated,
    // to prevent flickering during query refetches)
    if (
      authStatus === null ||
      (loading && authStatus !== AuthStatus.NotAuthenticated)
    ) {
      return null;
    }

    // If the user isn't signed in, but we haven't yet determined if they
    // have an offline user context, don't render anything yet
    if (loadingOfflineContext && authStatus !== AuthStatus.Authenticated) {
      return null;
    }

    // If there are auto-login params and the user isn't authenticated yet,
    // return null to show the loading state while the user is signed in
    // automatically in the background
    if (
      autoLoginParams &&
      authStatus !== AuthStatus.Authenticated &&
      !authenticatedUserUUID
    ) {
      return null;
    }

    let context: UserContextType = {
      authStatus: AuthStatus.NotAuthenticated,
      checkAuthStatus,
      isGuestMode: false,
      previousAuthStatus,
      refetch,
    };

    const supplierMap: {
      [supplierUUID: string]: UserContextSupplierFragment;
    } = {};
    const supplierDynamicConfigMap: SupplierDynamicConfigMap = {};
    const supplierEmployeeMap: {
      [supplierUUID: string]: UserContextSupplierEmployeeFragment;
    } = {};
    for (const supplier of userContextData?.businesses ?? []) {
      supplierMap[supplier.uuid] = supplier;
      supplierDynamicConfigMap[supplier.uuid] =
        supplier.supplier_metadata?.dynamic_config || {};
      for (const supplierEmployee of supplier.businesses_employees) {
        supplierEmployeeMap[supplier.uuid] = supplierEmployee;
      }
    }

    const translationsEnabled =
      userContextData?.whitelabel_config?.[0]?.translations_enabled ?? false;

    const user = userContextData?.employees_by_pk;
    if (user) {
      const employeeChats = userContextData?.employee_chats;
      if (employeeChats.length > 0) {
        // Hasura doesn't support distinct nested queries on a many-to-many
        // relationship, so it has to be done here
        const restaurantUUIDToChatUUIDsMap: {
          [restaurantUUID: string]: string[];
        } = {};

        const restaurantRelationships = employeeChats.reduce<
          EmployeeRestaurant[]
        >((result, ec) => {
          const chatUUID = ec.chat_uuid!;
          const restaurantUUID = ec.restaurant_uuid!;
          if (restaurantUUIDToChatUUIDsMap[restaurantUUID]) {
            restaurantUUIDToChatUUIDsMap[restaurantUUID].push(chatUUID);
          } else {
            restaurantUUIDToChatUUIDsMap[restaurantUUID] = [chatUUID];

            const supplierUUID = ec.supplier_uuid!;
            const isSupplierEmployee = Boolean(
              supplierEmployeeMap[supplierUUID],
            );

            const chatDynamicConfig: DynamicConfig = ec.chat_dynamic_config;
            const supplierDynamicConfig =
              supplierDynamicConfigMap[supplierUUID];

            const creditRequestConfig =
              chatDynamicConfig.credit_request_config ||
              supplierDynamicConfig.credit_request_config ||
              null;

            const showCreditRequest = creditRequestConfig
              ? determineCreditRequestVisibility(
                  isSupplierEmployee,
                  creditRequestConfig.viewable_by,
                )
              : // TODO: https://usepepper.atlassian.net/browse/DSR-2229
                (chatDynamicConfig.credit_request_enabled ??
                supplierDynamicConfig?.credit_request_enabled ??
                false);

            const salesRepConfig = isSupplierEmployee
              ? chatDynamicConfig.sales_rep_config ||
                supplierDynamicConfig?.sales_rep_config
              : null;

            const employeeConfig = EmployeeConfig.schema
              .catch(() => EmployeeConfig.empty())
              .parse(supplierEmployeeMap[supplierUUID]?.employee_config ?? {});

            const consumptionTax =
              chatDynamicConfig.consumption_tax ||
              supplierDynamicConfig?.consumption_tax ||
              null;
            const resolvedChatStatus = promotedRestaurantUUIDs.has(
              restaurantUUID,
            )
              ? ChatStatus.ACTIVE
              : (ec.status as ChatStatus);

            result.push({
              accountID:
                typeof ec.restaurant_account_id === 'string'
                  ? normalizeQuotes(ec.restaurant_account_id)
                  : null,
              addressString: normalizeQuotes(ec.restaurant_address!),
              appPaymentsEnabled: ec.app_payments_enabled!,
              carouselHoldout:
                chatDynamicConfig.catalog_carousel_holdout ??
                supplierDynamicConfig?.catalog_carousel_holdout ??
                false,
              categoryImagesEnabled:
                chatDynamicConfig.category_images_enabled ??
                supplierDynamicConfig?.category_images_enabled ??
                false,
              chatDynamicConfig,
              chatStatus: resolvedChatStatus,
              chatUUIDs: restaurantUUIDToChatUUIDsMap[restaurantUUID],
              commissionSettings: salesRepConfig?.commission_settings || null,
              consumptionTax: consumptionTax,
              countryCode:
                supplierMap[supplierUUID]?.address?.country_code || null,
              customerCostType: salesRepConfig?.customer_cost_type || null,
              displayItemPrices: canDisplayItemPrices(
                ec.display_item_prices!,
                resolvedChatStatus,
                chatDynamicConfig,
                supplierDynamicConfig,
                isSupplierEmployee,
              ),
              employeeRole:
                (ec.restaurant_employee_role as EmployeeRole) ?? null,
              hidePlaceholderImages:
                chatDynamicConfig.hide_placeholder_images ??
                supplierDynamicConfig?.hide_placeholder_images ??
                false,
              largeScreenModalHoldout:
                chatDynamicConfig.large_screen_modal_holdout ??
                supplierDynamicConfig?.large_screen_modal_holdout ??
                false,
              name: normalizeQuotes(ec.restaurant_name!),
              onDemandPaymentStatementEnabled:
                chatDynamicConfig.on_demand_payment_statement_enabled ??
                supplierDynamicConfig?.on_demand_payment_statement_enabled ??
                false,
              onHold: ec.on_hold!,
              orderReviewRequired: ec.order_review_required!,
              paymentsOnlyEnabled:
                chatDynamicConfig.payments_only_mode_enabled ??
                supplierDynamicConfig?.payments_only_mode_enabled ??
                false,
              payUponOrdering:
                chatDynamicConfig.pay_upon_ordering ??
                supplierDynamicConfig?.pay_upon_ordering ??
                false,
              requirePaymentMethodOnOrderPlacement:
                chatDynamicConfig.require_payment_method_on_order_placement ??
                supplierDynamicConfig?.require_payment_method_on_order_placement ??
                false,
              salesRepGrossProfitEnabled: Boolean(
                isSupplierEmployee &&
                  salesRepConfig?.override_mode ===
                    SalesRepOverrideMode.GROSS_PROFIT,
              ),
              salesRepIgnoreNegativeInGrossProfitTotal: Boolean(
                salesRepConfig?.calculation_settings
                  ?.ignore_negatives_in_gross_profit_total,
              ),
              salesRepModeEnabled: Boolean(
                isSupplierEmployee &&
                  salesRepConfig &&
                  salesRepConfig?.override_mode !==
                    SalesRepOverrideMode.DISABLED,
              ),
              salesRepPeakPriceProtection:
                salesRepConfig?.peak_price_protection ?? false,
              salesRepPriceFloorMode: salesRepConfig
                ? (employeeConfig.priceFloorMode ??
                  salesRepConfig.price_floor_mode ??
                  null)
                : null,
              salesRepPricingEnabled: Boolean(
                isSupplierEmployee &&
                  salesRepConfig?.override_mode === SalesRepOverrideMode.PRICE,
              ),
              showChatbot:
                chatDynamicConfig.is_chatbot_enabled ??
                supplierDynamicConfig?.is_chatbot_enabled ??
                false,
              showCreditRequest: showCreditRequest,
              showCrmActivities:
                isSupplierEmployee &&
                (chatDynamicConfig.crm_activities_enabled ??
                  supplierDynamicConfig?.crm_activities_enabled ??
                  false),
              showMenuCosting:
                chatDynamicConfig.menu_costing_enabled ??
                supplierDynamicConfig?.menu_costing_enabled ??
                false,
              showMenuOrderGuideGeneration:
                chatDynamicConfig.menu_og_generation_enabled ??
                supplierDynamicConfig?.menu_og_generation_enabled ??
                false,
              showOfflineMode:
                isOfflineModeAllowed &&
                (chatDynamicConfig.offline_mode_enabled ??
                  supplierDynamicConfig?.offline_mode_enabled ??
                  false),
              showPaymentLinks:
                chatDynamicConfig.payment_links_config?.enabled ??
                supplierDynamicConfig?.payment_links_config?.enabled ??
                false,
              showPaymentsTab: ec.show_app_payments_tab!,
              showProspectFinder:
                isSupplierEmployee &&
                (chatDynamicConfig.prospect_finder_enabled ??
                  supplierDynamicConfig?.prospect_finder_enabled ??
                  false),
              showTotalSalesDashboard:
                chatDynamicConfig.is_analytics_dashboard_enabled ??
                supplierDynamicConfig?.is_analytics_dashboard_enabled ??
                false,
              smartOrderEnabled:
                chatDynamicConfig.smart_order_enabled ??
                supplierDynamicConfig?.smart_order_enabled ??
                false,
              supplierName: supplierMap[supplierUUID]?.name ?? '',
              supplierPaymentsStatus:
                (supplierMap[supplierUUID]?.payments_status
                  ?.status as PaymentsStatus) ?? PaymentsStatus.Disabled,
              supplierUUID,
              tagPrintingEnabled:
                Platform.OS !== 'web' &&
                (chatDynamicConfig.tag_printing_config?.tag_printing_enabled ??
                  supplierDynamicConfig?.tag_printing_config
                    ?.tag_printing_enabled ??
                  false),
              timezone: ec.restaurant_timezone!,
              transactionFeeName:
                chatDynamicConfig.transaction_fee_name ??
                supplierDynamicConfig?.transaction_fee_name ??
                null,
              uuid: restaurantUUID,
            });
          }

          return result;
        }, []);

        const restaurantUUIDToRelationshipMap = restaurantRelationships.reduce<{
          [restaurantUUID: string]: EmployeeRestaurant;
        }>((result, relationship) => {
          result[relationship.uuid] = relationship;
          return result;
        }, {});

        const savedRestaurantRelationship = userSettings.selectedRestaurantUUID
          ? restaurantUUIDToRelationshipMap[userSettings.selectedRestaurantUUID]
          : null;
        // Use the saved relationship if it exists, otherwise use the first one
        const selectedRestaurantRelationship =
          savedRestaurantRelationship || restaurantRelationships[0];
        const selectedRestaurantUUID = selectedRestaurantRelationship.uuid;
        const selectedSupplierUUID =
          selectedRestaurantRelationship.supplierUUID;

        // Update saved selected relationship details if any of them have changed
        if (
          selectedRestaurantUUID !== userSettings.selectedRestaurantUUID ||
          selectedSupplierUUID !== userSettings.selectedSupplierUUID
        ) {
          setUserSettingsCallback({
            selectedRestaurantUUID,
            selectedSupplierUUID,
          });
        }

        const supplierScopedFields = deriveSupplierScopedUserFields({
          selectedRestaurantRelationship,
          supplierEmployeeMap,
          supplierMap,
          supplierUUID: selectedSupplierUUID,
        });

        const userOverrides = user.supplier_employee_overrides?.find(
          (seo) => seo.supplier_uuid === selectedSupplierUUID,
        );

        // User info is a combination of
        // - base user fields from response
        // - supplier employee overrides
        // - local user updates
        let resolvedUser = userOverrides
          ? {
              ...user,
              contact_info: {
                ...user.contact_info,
                email:
                  userOverrides.contact_emails[0] ?? user.contact_info.email,
                phone_number:
                  userOverrides.contact_phone_numbers[0] ??
                  user.contact_info.phone_number,
              },
              name: userOverrides.name ?? user.name,
            }
          : user;
        if (localUserUpdates) {
          resolvedUser = {
            ...resolvedUser,
            contact_info: {
              ...resolvedUser.contact_info,
              email:
                localUserUpdates.contactEmail ??
                resolvedUser.contact_info.email,
              login_email:
                localUserUpdates.loginEmail ??
                resolvedUser.contact_info.login_email,
              phone_number:
                localUserUpdates.contactPhoneNumber ??
                resolvedUser.contact_info.phone_number,
            },
            locale: localUserUpdates.locale ?? resolvedUser.locale,
            name: localUserUpdates.name ?? resolvedUser.name,
          };
        }

        context = {
          ...context,
          authStatus:
            authStatus === AuthStatus.Offline
              ? AuthStatus.Offline
              : AuthStatus.Authenticated,
          canCreateAccount: supplierScopedFields.canCreateAccount,
          chatRestaurantMap: Object.keys(
            restaurantUUIDToChatUUIDsMap,
          ).reduce<ChatRestaurantMap>((result, restaurantUUID) => {
            for (const chatUUID of restaurantUUIDToChatUUIDsMap[
              restaurantUUID
            ]) {
              result[chatUUID] = restaurantUUID;
            }

            return result;
          }, {}),
          commissionSettings: supplierScopedFields.commissionSettings,
          creditRequestSettings: supplierScopedFields.creditRequestSettings,
          employeeCan: supplierScopedFields.employeeCan,
          overrideChatStatus,
          restaurantRelationships,
          restaurantUUIDToRelationshipMap,
          selectedRestaurantRelationship,
          selectedRestaurantUUID,
          selectRestaurant: (restaurantUUID: string | null) => {
            setUserSettingsCallback({
              selectedRestaurantUUID: restaurantUUID,
              selectedSupplierUUID: restaurantUUID
                ? restaurantUUIDToRelationshipMap[restaurantUUID]
                    ?.supplierUUID || null
                : null,
            });
          },
          smartOrderSettings: supplierScopedFields.smartOrderSettings,
          supplierDynamicConfigMap,
          supplierEmployeeMap,
          supplierEmployeeRole: supplierScopedFields.supplierEmployeeRole,
          supplierMap,
          translationsEnabled,
          updateUserLocally,
          user: resolvedUser,
        };

        // Auth status is now managed by AuthProvider
      } else {
        context = {
          ...context,
          authStatus: AuthStatus.NotAuthorized,
          employeeCan: () => false,
        };
      }
    }

    // Default moment to the selected restaurant's timezone globally
    moment.tz.setDefault(
      context.authStatus === AuthStatus.Authenticated ||
        context.authStatus === AuthStatus.Offline
        ? context.selectedRestaurantRelationship?.timezone
        : undefined,
    );

    // Tracker initialization
    const st = Fennel.getInstance();
    if (
      context.authStatus === AuthStatus.Authenticated ||
      context.authStatus === AuthStatus.Offline
    ) {
      st.initialize(
        {
          businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
          employeeUUID: authenticatedUserUUID || undefined,
          restaurantUUID: context.selectedRestaurantRelationship.uuid,
          supplierEmployeeRole: context.supplierEmployeeRole ?? undefined,
          supplierUUID: context.selectedRestaurantRelationship.supplierUUID,
          userRole:
            context.selectedRestaurantRelationship.employeeRole ??
            EmployeeRole.User,
        },
        Analytics,
      );
      const selectedSupplierConfig =
        supplierDynamicConfigMap[
          context.selectedRestaurantRelationship.supplierUUID
        ];
      const selectedChatConfig =
        context.selectedRestaurantRelationship.chatDynamicConfig;
      performanceTracingRuntimeConfig.set(
        resolvePerformanceTracingRuntimeConfig(
          getValueFromConfigs(
            'performance_tracing_config',
            selectedSupplierConfig,
            selectedChatConfig,
          ),
          Boolean(
            getValueFromConfigs(
              'limited_app_enabled',
              selectedSupplierConfig,
              selectedChatConfig,
            ),
          ),
        ),
      );
      sentryTracingRuntimeConfig.set(
        resolveSentryTracingRuntimeConfig(
          getValueFromConfigs(
            'sentry_config',
            selectedSupplierConfig,
            selectedChatConfig,
          ),
        ),
      );
    } else {
      performanceTracingRuntimeConfig.reset();
      sentryTracingRuntimeConfig.reset();
    }

    return context;
  }, [
    loading,
    loadingOfflineContext,
    authStatus,
    autoLoginParams,
    authenticatedUserUUID,
    promotedRestaurantUUIDs,
    overrideChatStatus,
    updateUserLocally,
    refetch,
    localUserUpdates,
    userContextData,
    userSettings.selectedRestaurantUUID,
    userSettings.selectedSupplierUUID,
    setUserSettingsCallback,
    checkAuthStatus,
    previousAuthStatus,
  ]);

  useSentrySessionTracking({
    isOfflineModeActive,
    relationship:
      value && 'selectedRestaurantRelationship' in value
        ? value.selectedRestaurantRelationship
        : null,
    supplierEmployeeRole:
      value && 'supplierEmployeeRole' in value
        ? value.supplierEmployeeRole
        : null,
  });

  return (
    <UserSettingsContext.Provider value={settingsValue}>
      <UserContext.Provider value={value}>{children}</UserContext.Provider>
    </UserSettingsContext.Provider>
  );
};

export const GuestUserContextProvider: React.FC<Props> = ({ children }) => {
  const [guestUserSettings, setGuestUserSettings] = useState<UserSettings>(
    INITIAL_USER_SETTINGS,
  );
  const { publicRestaurant, translationsEnabled } = useAuthStackContext();

  const setGuestUserSettingsCallback = useCallback(
    async (settings: Partial<UserSettings>) => {
      try {
        const adapter = new UserSettingsAdapter<UserSettings>(
          GUEST_USER_SETTINGS_KEY,
        );
        const savedUserSettings = await adapter.fetch();
        const nextUserSettings = {
          ...(savedUserSettings || INITIAL_USER_SETTINGS),
          ...settings,
        };

        await adapter.save(nextUserSettings);

        setGuestUserSettings(nextUserSettings);
      } catch (e) {
        captureException(e);
      }
    },
    [],
  );

  const settingsValue = useMemo<UserSettingsContextType>(() => {
    return {
      alertsSeen: INITIAL_ALERTS_SEEN,
      catalogSortOrder:
        guestUserSettings.catalogSortOrder || ItemSortOrder.Alphabetical,
      inInboundTrainingMode: false,
      isDescriptionVisible: guestUserSettings.isDescriptionVisible ?? true,
      largeItemListDisplayMode:
        guestUserSettings.largeItemListDisplayMode || ListDisplayMode.List,
      searchSortOrder:
        guestUserSettings.searchSortOrder || SearchSortOrder.Relevance,
      setAlertSeen: () => {},
      setCatalogSortOrder: (sortOrder: ItemSortOrder) => {
        setGuestUserSettingsCallback({ catalogSortOrder: sortOrder });
      },
      setInInboundTrainingMode: () => {},
      setIsDescriptionVisible: (isDescriptionVisible: boolean) => {
        setGuestUserSettingsCallback({ isDescriptionVisible });
      },
      setLargeItemListDisplayMode: (displayMode: ListDisplayMode) => {
        setGuestUserSettingsCallback({ largeItemListDisplayMode: displayMode });
      },
      setSearchSortOrder: (sortOrder: SearchSortOrder) => {
        setGuestUserSettingsCallback({ searchSortOrder: sortOrder });
      },
      setShouldFlashlightActivate: (shouldFlashlightActivate: boolean) => {
        setGuestUserSettingsCallback({ shouldFlashlightActivate });
      },
      setShowSalesRepUI: (showSalesRepUI: boolean) => {
        setGuestUserSettingsCallback(
          showSalesRepUI
            ? {
                largeItemListDisplayMode: ListDisplayMode.List,
                showSalesRepUI,
              }
            : { showSalesRepUI },
        );
      },
      shouldFlashlightActivate: guestUserSettings.shouldFlashlightActivate,
      showSalesRepUI: guestUserSettings.showSalesRepUI,
    };
  }, [guestUserSettings, setGuestUserSettingsCallback]);

  const value = useMemo<UserContextType | null>(() => {
    const restaurantRelationship: EmployeeRestaurant = {
      accountID: null,
      addressString: publicRestaurant
        ? normalizeQuotes(publicRestaurant.address)
        : '',
      appPaymentsEnabled: false,
      carouselHoldout: false,
      categoryImagesEnabled: publicRestaurant?.categoryImagesEnabled ?? false,
      chatDynamicConfig: publicRestaurant?.chatDynamicConfig ?? {},
      chatStatus: ChatStatus.ACTIVE,
      chatUUIDs: publicRestaurant ? [publicRestaurant.chatUUID] : [],
      commissionSettings: null,
      consumptionTax: null,
      countryCode: publicRestaurant?.countryCode ?? null,
      customerCostType: null,
      displayItemPrices: false,
      employeeRole: null,
      hidePlaceholderImages: publicRestaurant?.hidePlaceholderImages ?? false,
      largeScreenModalHoldout: false,
      name: publicRestaurant ? normalizeQuotes(publicRestaurant.name) : '',
      onDemandPaymentStatementEnabled: false,
      onHold: false,
      orderReviewRequired: false,
      paymentsOnlyEnabled: false,
      payUponOrdering: false,
      requirePaymentMethodOnOrderPlacement: false,
      salesRepGrossProfitEnabled: false,
      salesRepIgnoreNegativeInGrossProfitTotal: false,
      salesRepModeEnabled: false,
      salesRepPeakPriceProtection: false,
      salesRepPriceFloorMode: null,
      salesRepPricingEnabled: false,
      showChatbot: false,
      showCreditRequest: false,
      showCrmActivities: false,
      showMenuCosting: false,
      showMenuOrderGuideGeneration: false,
      showOfflineMode: false,
      showPaymentLinks: false,
      showPaymentsTab: false,
      showProspectFinder: false,
      showTotalSalesDashboard: false,
      smartOrderEnabled: false,
      supplierName: '',
      supplierPaymentsStatus: PaymentsStatus.Disabled,
      supplierUUID: publicRestaurant ? publicRestaurant!.supplierUUID : '',
      tagPrintingEnabled: false,
      timezone: publicRestaurant ? publicRestaurant!.timezone : '',
      transactionFeeName: null,
      uuid: publicRestaurant ? publicRestaurant!.restaurantUUID : '',
    };

    // Default moment to the device's timezone
    moment.tz.setDefault(publicRestaurant?.timezone ?? moment.tz.guess());

    // Tracker initialization
    const st = Fennel.getInstance();
    st.initialize(
      {
        businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
        restaurantUUID: publicRestaurant
          ? publicRestaurant.restaurantUUID
          : undefined,
        supplierUUID: publicRestaurant?.supplierUUID,
        userRole: EmployeeRole.User,
      },
      Analytics,
    );
    sentryTracingRuntimeConfig.set(
      resolveSentryTracingRuntimeConfig(
        getValueFromConfigs(
          'sentry_config',
          publicRestaurant?.supplierDynamicConfig,
          publicRestaurant?.chatDynamicConfig,
        ),
      ),
    );
    performanceTracingRuntimeConfig.set(
      resolvePerformanceTracingRuntimeConfig(
        getValueFromConfigs(
          'performance_tracing_config',
          publicRestaurant?.supplierDynamicConfig,
          publicRestaurant?.chatDynamicConfig,
        ),
        Boolean(
          getValueFromConfigs(
            'limited_app_enabled',
            publicRestaurant?.supplierDynamicConfig,
            publicRestaurant?.chatDynamicConfig,
          ),
        ),
      ),
    );

    return {
      authStatus: AuthStatus.NotAuthenticated,
      checkAuthStatus: async () => {},
      employeeCan: () => false,
      isGuestMode: true,
      previousAuthStatus: null,
      refetch: () => {},
      restaurantRelationships: [restaurantRelationship],
      restaurantUUIDToRelationshipMap: {
        [restaurantRelationship.uuid]: restaurantRelationship,
      },
      selectedRestaurantRelationship: restaurantRelationship,
      selectedRestaurantUUID: restaurantRelationship.uuid,
      supplierDynamicConfigMap: publicRestaurant
        ? {
            [publicRestaurant.supplierUUID]:
              publicRestaurant.supplierDynamicConfig,
          }
        : {},
      translationsEnabled,
    };
  }, [publicRestaurant, translationsEnabled]);

  return (
    <UserSettingsContext.Provider value={settingsValue}>
      <UserContext.Provider value={value}>{children}</UserContext.Provider>
    </UserSettingsContext.Provider>
  );
};

export const useMaybeUserContext = () => useContext(UserContext);

export const useUserContext = () => {
  const userContext = useContext(UserContext);

  if (!userContext) {
    throw new Error('useUserContext requires a UserContextProvider');
  }

  // For non-guest users, validate they're authenticated or offline
  if (
    !userContext.isGuestMode &&
    userContext.authStatus !== AuthStatus.Authenticated &&
    userContext.authStatus !== AuthStatus.Offline
  ) {
    throw new Error('useUserContext user is not authenticated and not a guest');
  }

  // Guest mode bypasses authentication
  if (userContext.isGuestMode) {
    return userContext as GuestUserContextType;
  }

  return userContext as AuthenticatedUserContextType;
};

export const useAuthenticatedUserContext = () => {
  const userContext = useUserContext();
  if (userContext.isGuestMode) {
    throw new Error('useUserContext user is not authenticated');
  }
  return userContext as AuthenticatedUserContextType;
};

export const useDefaultUserContext = () => {
  const maybeBaseUserContext = useMaybeUserContext();
  if (!maybeBaseUserContext) {
    throw new Error('useDefaultUserContext requires a UserContextProvider');
  }

  return maybeBaseUserContext;
};

export const useSmartOrderAuthenticatedUserContext = () => {
  const userContext = useUserContext();
  if (userContext.isGuestMode) {
    throw new Error('useUserContext user is not authenticated');
  }
  return userContext as SmartOrderAuthenticatedUserContextType;
};

export const useUserSettingsContext = () => {
  const userContext = useContext(UserSettingsContext);
  if (!userContext) {
    throw new Error(
      'useUserSettingsContext requires a UserSettingsContextProvider',
    );
  }

  return userContext;
};
